import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  MapPin,
  Store,
  UserCheck,
  Settings,
  ArrowLeft,
  HelpCircle,
  RefreshCw,
} from 'lucide-react';
import apiServices from '../../services/api';
import {
  UsersManagement,
  SellersManagement,
  RepresentativesManagement,
  StoresManagement,
  CitiesManagement,
  ChainsManagement,
  SystemSettings,
  QuestionsAdmin,
  WizardPreview,
} from './index';
import type { TabConfig } from '../../types/api';
import SurveyManager from './SurveyManager';

type AdminSection =
  | 'dashboard'
  | 'users'
  | 'sellers'
  | 'representatives'
  | 'stores'
  | 'cities'
  | 'chains'
  | 'questions'
  | 'surveys'
  | 'settings';

interface AdminPanelProps {
  onBack?: () => void;
}

const AdminPanel = ({ onBack }: AdminPanelProps): ReactElement => {
  const [currentSection, setCurrentSection] =
    useState<AdminSection>('dashboard');
  const [previewConfig, setPreviewConfig] = useState<TabConfig[] | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardStats();
  }, []);
  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiServices.admin.getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Verificar que el usuario tenga permisos de administrador
  const user = ((): { name?: string; role?: string } | null => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        name?: string;
        role?: string;
      } | null;
    } catch {
      return null;
    }
  })();

  const hasAdminAccess =
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'GENERAL_MANAGER';

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <div className="text-xl font-medium text-gray-700 mb-2">
            Acceso Restringido
          </div>
          <div className="text-sm text-gray-500 mb-6">
            Solo los administradores pueden acceder al panel de administración
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-medium transition-colors"
            >
              Volver al inicio
            </button>
          )}
        </div>
      </div>
    );
  }

  const adminSections = [
    {
      id: 'users' as AdminSection,
      title: 'Usuarios',
      description: 'Gestionar usuarios del sistema',
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      id: 'sellers' as AdminSection,
      title: 'Vendedoras',
      description: 'Gestionar vendedoras y asignaciones',
      icon: UserCheck,
      color: 'bg-green-500',
    },
    {
      id: 'representatives' as AdminSection,
      title: 'Representantes',
      description: 'Gestionar representantes de venta',
      icon: Users,
      color: 'bg-purple-500',
    },
    {
      id: 'stores' as AdminSection,
      title: 'Puntos de Venta',
      description: 'Gestionar tiendas y sucursales',
      icon: Store,
      color: 'bg-orange-500',
    },
    {
      id: 'cities' as AdminSection,
      title: 'Ciudades',
      description: 'Gestionar ciudades por zona',
      icon: MapPin,
      color: 'bg-teal-500',
    },
    {
      id: 'chains' as AdminSection,
      title: 'Cadenas',
      description: 'Gestionar cadenas comerciales',
      icon: Building2,
      color: 'bg-indigo-500',
    },
    {
      id: 'questions' as AdminSection,
      title: 'Gestión de Preguntas',
      description: 'Configurar wizard de evaluación',
      icon: HelpCircle,
      color: 'bg-rose-500',
    },
    {
      id: 'surveys' as AdminSection,
      title: 'Gestión de Surveys',
      description: 'Crear y gestionar tabs y preguntas',
      icon: HelpCircle,
      color: 'bg-pink-500',
    },
    {
      id: 'settings' as AdminSection,
      title: 'Configuración',
      description: 'Configuración del sistema',
      icon: Settings,
      color: 'bg-gray-500',
    },
  ];

  const handlePreviewWizard = (config: TabConfig[]): void => {
    setPreviewConfig(config);
    setShowPreview(true);
  };

  const renderCurrentSection = (): ReactElement => {
    if (showPreview && previewConfig) {
      return (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Vista Previa del Wizard
              </h2>
              <button
                onClick={() => {
                  setShowPreview(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver a Configuración
              </button>
            </div>
          </div>
          <div className="flex-1">
            <WizardPreview config={previewConfig} />
          </div>
        </div>
      );
    }

    switch (currentSection) {
      case 'users':
        return <UsersManagement />;
      case 'sellers':
        return <SellersManagement />;
      case 'representatives':
        return <RepresentativesManagement />;
      case 'stores':
        return <StoresManagement />;
      case 'cities':
        return <CitiesManagement />;
      case 'chains':
        return <ChainsManagement />;
      case 'questions':
        return <QuestionsAdmin onPreview={handlePreviewWizard} />;
      case 'surveys':
        return <SurveyManager />;
      case 'settings':
        return <SystemSettings />;
      default:
        return (
          <div className="space-y-6">
            <div className="mb-6 sm:mb-8">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                Panel de Administración
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Gestiona todos los aspectos del sistema desde aquí
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {adminSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      setCurrentSection(section.id);
                    }}
                    className="bg-white p-6 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center mb-4">
                      <div
                        className={`${section.color} p-3 rounded-lg text-white group-hover:scale-110 transition-transform duration-200`}
                      >
                        <IconComponent className="w-6 h-6" />
                      </div>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {section.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {section.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Estadísticas rápidas */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Estadísticas del Sistema
                </h2>
                <button
                  onClick={loadDashboardStats}
                  disabled={loading}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Actualizar estadísticas"
                >
                  <RefreshCw
                    className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              {loading && !stats ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="bg-white p-4 rounded-lg border border-gray-200 animate-pulse"
                    >
                      <div className="h-8 bg-gray-200 rounded mb-2 w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                  ))}
                </div>
              ) : stats ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600">
                        {stats.users.active}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Usuarios Activos
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-500 mt-1">
                        {stats.users.total} total
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="text-2xl font-bold text-green-600">
                        {stats.hierarchy.sellers}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Vendedoras
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="text-2xl font-bold text-purple-600">
                        {stats.hierarchy.representatives}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Representantes
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="text-2xl font-bold text-orange-600">
                        {stats.hierarchy.stores}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Puntos de Venta
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {stats.coverage.percentage}% con representante
                      </div>
                    </div>
                  </div>

                  {/* Stats adicionales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <div className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                        Jerarquía
                      </div>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Zonas:</span>
                          <span className="font-medium">
                            {stats.hierarchy.zones}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ciudades:</span>
                          <span className="font-medium">
                            {stats.hierarchy.cities}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cadenas:</span>
                          <span className="font-medium">
                            {stats.hierarchy.chains}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-lg font-semibold text-gray-900 mb-3">
                        Reportes
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-medium">
                            {stats.reports.total}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Este mes:</span>
                          <span className="font-medium">
                            {stats.reports.thisMonth}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score promedio:</span>
                          <span className="font-medium">
                            {stats.reports.avgScore}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-lg font-semibold text-gray-900 mb-3">
                        Cobertura
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Con representante:
                          </span>
                          <span className="font-medium text-green-600">
                            {stats.coverage.storesWithRep}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Sin representante:
                          </span>
                          <span className="font-medium text-red-600">
                            {stats.coverage.storesWithoutRep}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Porcentaje:</span>
                          <span className="font-medium">
                            {stats.coverage.percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        );
    }
  };

  const getCurrentSectionTitle = (): string => {
    if (showPreview) return 'Vista Previa del Wizard';
    const section = adminSections.find((s) => s.id === currentSection);
    return section?.title ?? 'Panel de Administración';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {(currentSection !== 'dashboard' || showPreview) && (
              <button
                onClick={() => {
                  if (showPreview) {
                    setShowPreview(false);
                  } else {
                    setCurrentSection('dashboard');
                  }
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Volver al dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {getCurrentSectionTitle()}
              </h1>
              {user?.name && (
                <p className="text-sm text-gray-600">
                  {user.name} •{' '}
                  {user.role === 'SYSTEM_ADMIN'
                    ? 'Administrador del Sistema'
                    : user.role === 'GENERAL_MANAGER'
                    ? 'Gerente General'
                    : 'Administrador'}
                </p>
              )}
            </div>
          </div>

          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Salir de Admin</span>
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="p-6">{renderCurrentSection()}</main>
    </div>
  );
};

export default AdminPanel;
